import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'

import { SearchInput } from '@/components/forms/search-input'
import { cn } from '@/lib/cn'

import { filterTimezoneOptions, type TimezoneSelectOption } from './timezone-select-options'

const popupGap = 4
const popupMaxHeight = 360
const viewportPadding = 8

interface PopupPosition {
  style: CSSProperties
}

interface ScrollableOption {
  scrollIntoView?: (options?: ScrollIntoViewOptions) => void
}

function optionId(listboxId: string, index: number) {
  return `${listboxId}-option-${String(index)}`
}

export function TimezoneSelect({
  ariaLabel,
  className,
  disabled = false,
  emptyText,
  onValueChange,
  options,
  searchLabel,
  searchPlaceholder,
  value,
}: {
  ariaLabel: string
  className?: string
  disabled?: boolean
  emptyText: string
  onValueChange: (value: string) => void
  options: TimezoneSelectOption[]
  searchLabel: string
  searchPlaceholder: string
  value: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const filteredOptions = useMemo(() => filterTimezoneOptions(options, query), [options, query])
  const selectedOption = options.find((option) => option.value === value) ?? options[0]
  const activeOptionId =
    isOpen && filteredOptions[activeIndex] ? optionId(listboxId, activeIndex) : undefined

  useEffect(() => {
    if (!isOpen) return undefined

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    window.requestAnimationFrame(() => {
      popupRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus()
    })
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen) return undefined

    function updatePopupPosition() {
      const trigger = triggerRef.current
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const belowSpace = window.innerHeight - rect.bottom - popupGap - viewportPadding
      const aboveSpace = rect.top - popupGap - viewportPadding
      const openUpwards = belowSpace < Math.min(240, popupMaxHeight) && aboveSpace > belowSpace
      const availableHeight = openUpwards ? aboveSpace : belowSpace
      const maxHeight = Math.max(180, Math.min(popupMaxHeight, availableHeight))
      const width = Math.min(Math.max(rect.width, 420), window.innerWidth - viewportPadding * 2)
      const left = Math.max(
        viewportPadding,
        Math.min(rect.left, window.innerWidth - width - viewportPadding),
      )

      setPopupPosition({
        style: {
          ...(openUpwards
            ? { bottom: window.innerHeight - rect.top + popupGap }
            : { top: rect.bottom + popupGap }),
          left,
          maxHeight,
          width,
        },
      })
    }

    updatePopupPosition()
    window.addEventListener('resize', updatePopupPosition)
    window.addEventListener('scroll', updatePopupPosition, true)
    return () => {
      window.removeEventListener('resize', updatePopupPosition)
      window.removeEventListener('scroll', updatePopupPosition, true)
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (!activeOptionId) return
    const activeOption = document.getElementById(activeOptionId) as ScrollableOption | null
    activeOption?.scrollIntoView?.({ block: 'nearest' })
  }, [activeOptionId])

  function closeAndRestoreFocus() {
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  function openPicker() {
    if (disabled || options.length === 0) return
    setQuery('')
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((option) => option.value === value),
      ),
    )
    setIsOpen(true)
  }

  function selectOption(option: TimezoneSelectOption) {
    onValueChange(option.value)
    closeAndRestoreFocus()
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      closeAndRestoreFocus()
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (filteredOptions.length > 0) {
        setActiveIndex((current) => Math.min(current + 1, filteredOptions.length - 1))
      }
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => Math.max(current - 1, 0))
      return
    }
    if (event.key === 'Enter' && filteredOptions[activeIndex]) {
      event.preventDefault()
      selectOption(filteredOptions[activeIndex])
    }
  }

  return (
    <div className={cn('relative h-[38px] w-full text-sm', className)}>
      <button
        aria-controls={isOpen ? listboxId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className={cn(
          'flex h-full w-full min-w-0 items-center rounded-lg border pl-3 pr-9 text-left shadow-[var(--shadow-control)] transition-colors outline-none',
          disabled
            ? 'cursor-not-allowed border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] shadow-none'
            : [
                'cursor-pointer border-[var(--color-control-border)] bg-white text-[var(--color-text)] hover:border-[var(--color-control-border-hover)]',
                'focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-soft)]',
              ],
        )}
        disabled={disabled}
        onClick={() => {
          if (isOpen) setIsOpen(false)
          else openPicker()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setIsOpen(false)
            return
          }
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openPicker()
          }
        }}
        ref={triggerRef}
        role="combobox"
        type="button"
      >
        <span className="block min-w-0 flex-1 truncate" title={selectedOption.label}>
          {selectedOption.label}
        </span>
      </button>
      <svg
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-1/2 right-3 size-3 -translate-y-1/2 transition-transform',
          disabled ? 'text-[rgba(26,28,25,0.28)]' : 'text-[var(--color-text-muted)]',
          isOpen ? 'rotate-180' : null,
        )}
        fill="none"
        viewBox="0 0 12 12"
      >
        <path
          d="m3 4.5 3 3 3-3"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
      {isOpen && popupPosition
        ? createPortal(
            <div
              className="fixed z-[120] flex overflow-hidden rounded-lg border border-[var(--color-panel-border)] bg-white shadow-[0_12px_36px_rgba(26,28,25,0.16)]"
              ref={popupRef}
              style={popupPosition.style}
            >
              <div className="flex min-h-0 w-full flex-col">
                <div className="border-b border-[var(--color-divider)] p-2.5">
                  <SearchInput
                    aria-activedescendant={activeOptionId}
                    aria-controls={listboxId}
                    aria-label={searchLabel}
                    onChange={(event) => {
                      setQuery(event.target.value)
                      setActiveIndex(0)
                    }}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={searchPlaceholder}
                    value={query}
                  />
                </div>
                <div className="min-h-0 overflow-y-auto py-1" id={listboxId} role="listbox">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                      const selected = option.value === value
                      const active = index === activeIndex
                      return (
                        <button
                          aria-selected={selected}
                          className={cn(
                            'flex min-h-[52px] w-full cursor-pointer items-center gap-3 px-3 py-2 text-left outline-none',
                            active ? 'bg-[var(--color-hover-muted)]' : null,
                            selected
                              ? 'bg-[rgba(30,95,255,0.08)] text-[var(--color-primary)]'
                              : 'text-[var(--color-text)]',
                          )}
                          id={optionId(listboxId, index)}
                          key={option.value}
                          onClick={() => selectOption(option)}
                          onPointerMove={() => setActiveIndex(index)}
                          role="option"
                          type="button"
                        >
                          <span className="min-w-0 flex-1">
                            <span
                              className="block truncate text-sm font-medium"
                              title={option.label}
                            >
                              {option.label}
                            </span>
                            <span
                              className="mt-0.5 block truncate text-xs leading-4 text-[var(--color-text-muted)]"
                              title={option.description}
                            >
                              {option.description}
                            </span>
                          </span>
                          {selected ? (
                            <svg
                              aria-hidden
                              className="size-4 shrink-0"
                              fill="none"
                              viewBox="0 0 16 16"
                            >
                              <path
                                d="m3.5 8.5 3 3 6-7"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                              />
                            </svg>
                          ) : null}
                        </button>
                      )
                    })
                  ) : (
                    <div className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
                      {emptyText}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
